import {
  DndContext,
  DragOverlay,
  PointerSensor,
  defaultDropAnimation,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import PropType from 'prop-types';
import { useMemo, useState } from 'react';
import BaseTable from '../BaseTable';
import { SortableRowHandleContext } from './row-handle-context';

export { default as DraggableRowHandle } from './DraggableRowHandle';

const DraggableBody = (bodyProps) => <tbody {...bodyProps} />;

const buildOverlayCells = (columns, record, rowIndex) => {
  if (!columns?.length || !record) {
    return null;
  }
  return columns.map((col, index) => {
    const key = col.key ?? col.dataIndex ?? index;
    const { dataIndex, render: colRender, width } = col;
    const text =
      dataIndex !== undefined && dataIndex !== null
        ? record[dataIndex]
        : undefined;
    const content =
      typeof colRender === 'function'
        ? colRender(text, record, rowIndex)
        : text;
    return (
      <td
        key={String(key)}
        style={width ? { width } : undefined}
        className="ant-table-cell">
        {content}
      </td>
    );
  });
};

const DragOverlayTable = ({ columns, record, overlayClassName }) => (
  <table
    className={['draggable-table-overlay', overlayClassName]
      .filter(Boolean)
      .join(' ')}
    role="presentation">
    <tbody>
      <tr className="draggable-table-overlay__tr row-dragging">
        {buildOverlayCells(columns, record, 0)}
      </tr>
    </tbody>
  </table>
);

const SortableRow = ({
  children,
  style: rowStyle,
  className,
  ...restProps
}) => {
  const rowId = restProps['data-row-key'];
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rowId });

  const style = {
    ...rowStyle,
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { opacity: 0.35 } : {}),
  };

  const handleValue = useMemo(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners],
  );

  const trClassName = ['draggable-row', className].filter(Boolean).join(' ');

  return (
    <SortableRowHandleContext.Provider value={handleValue}>
      <tr
        ref={setNodeRef}
        style={style}
        className={trClassName}
        {...attributes}
        {...restProps}>
        {children}
      </tr>
    </SortableRowHandleContext.Provider>
  );
};

const DraggableTable = ({
  dataSource,
  rowKey = 'id',
  onDrag,
  columns,
  overlayClassName,
  components: userComponents,
  ...props
}) => {
  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
  );
  const items = dataSource.map((record) => record[rowKey]);

  const activeRecord = useMemo(
    () => dataSource.find((row) => row[rowKey] === activeId),
    [dataSource, rowKey, activeId],
  );

  const onDragStart = ({ active }) => {
    setActiveId(active.id);
  };

  const onDragEnd = ({ active, over }) => {
    setActiveId(null);
    if (over && active.id !== over.id) {
      const oldIndex = dataSource.findIndex(
        (record) => record[rowKey] === active.id,
      );
      const newIndex = dataSource.findIndex(
        (record) => record[rowKey] === over.id,
      );
      const oldRecord = dataSource[oldIndex];
      const newOrder = newIndex + 1;
      onDrag(oldRecord, newOrder);
    }
  };

  const onDragCancel = () => {
    setActiveId(null);
  };

  const mergedComponents = useMemo(
    () => ({
      ...userComponents,
      body: {
        ...userComponents?.body,
        wrapper: DraggableBody,
        row: SortableRow,
      },
    }),
    [userComponents],
  );

  return (
    <DndContext
      sensors={sensors}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragCancel={onDragCancel}>
      <SortableContext items={items} strategy={verticalListSortingStrategy}>
        <BaseTable
          dataSource={dataSource}
          rowKey={rowKey}
          columns={columns}
          {...props}
          components={mergedComponents}
        />
      </SortableContext>
      <DragOverlay dropAnimation={defaultDropAnimation}>
        {activeRecord ? (
          <DragOverlayTable
            columns={columns}
            record={activeRecord}
            overlayClassName={overlayClassName}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

DraggableTable.propTypes = {
  dataSource: PropType.arrayOf(PropType.shape({})),
  rowKey: PropType.string,
  onDrag: PropType.func,
  columns: PropType.arrayOf(PropType.shape({})),
  overlayClassName: PropType.string,
  components: PropType.shape({}),
};

DraggableTable.defaultProps = {
  dataSource: [],
  rowKey: 'id',
  onDrag: () => {},
  columns: undefined,
  overlayClassName: '',
  components: undefined,
};

export default DraggableTable;
